// A seção do descarte automático. (#880)

import { useState, type ReactNode } from 'react';
import { Estado } from '@/shared/ui/estados/estados';
import { useDlq } from '@/shared/api/consultas';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import { CampoSelecao, Cartao, Tabela } from '@/shared/ui';

export function SecaoDescarte(): ReactNode {
  const [razao, definirRazao] = useState('');
  const consulta = useDlq(razao === '' ? {} : { razao });

  return (
    <section className="admin__secao" aria-label="Fila de descarte">
      <p className="admin__apoio">
        Eventos que falharam e desistiram de tentar. O que importa aqui não é quantos são — é há
        quanto tempo estão parados.
      </p>

      <Estado
        consulta={consulta}
        forma="lista"
        linhas={4}
        vazio={{
          quando: (p) => p.resumo.profundidade === 0,
          titulo: 'Nada falhou por aqui',
          porque: 'É o estado esperado. Quando um evento desistir de tentar, ele aparece aqui.',
        }}
      >
        {(pagina) => (
          <>
            {/*
             * O resumo vem ANTES da lista e a idade vem ao lado da
             * profundidade, porque a Armadilha da D10 é medir só quantos são:
             * fila rasa e velha parece saudável e significa que ninguém olha.
             */}
            <dl className="admin__resumo">
              <Cartao>
                <dt>Na fila</dt>
                <dd>{pagina.resumo.profundidade}</dd>
              </Cartao>
              <Cartao parte={pagina.resumo.antigos > 0 ? 'admin__resumo--alerta' : undefined}>
                <dt>Parados há mais de uma semana</dt>
                <dd>{pagina.resumo.antigos}</dd>
              </Cartao>
              <Cartao>
                <dt>O mais antigo</dt>
                <dd>
                  {pagina.resumo.idade_do_mais_velho_em_horas === null
                    ? '—'
                    : idadeEmHorasPorExtenso(pagina.resumo.idade_do_mais_velho_em_horas)}
                </dd>
              </Cartao>
              <Cartao>
                <dt>Falha mais comum</dt>
                <dd>
                  {pagina.resumo.razao_mais_comum === null
                    ? '—'
                    : nomeDaRazao(pagina.resumo.razao_mais_comum)}
                </dd>
              </Cartao>
            </dl>

            <CampoSelecao
              rotulo="Filtrar por falha"
              tamanho="miudo"
              value={razao}
              onChange={(e) => definirRazao(e.target.value)}
            >
              <option value="">Todas</option>
              {RAZOES.map((r) => (
                <option key={r.chave} value={r.chave}>
                  {r.nome}
                </option>
              ))}
            </CampoSelecao>

            {pagina.itens.length === 0 ? (
              <p className="admin__nota">Nenhum item com esse filtro.</p>
            ) : (
              <>
                {/*
                Cinco colunas em 390px rolam de lado, e a falha — que é o
                assunto — some junto. No celular a falha vira o título da linha
                e "parado há" vai para a direita, que é a leitura da fila:
                o que quebrou, e há quanto tempo ninguém olha.
              */}
                <div className="admin__so-mouse">
                  <Tabela
                    densa
                    rotulo="Fila de descarte"
                    legenda="Do mais antigo para o mais recente — a ordem que mostra abandono."
                    colunas={[
                      { chave: 'parado', rotulo: 'Parado há' },
                      { chave: 'falha', rotulo: 'Falha' },
                      { chave: 'origem', rotulo: 'Origem' },
                      { chave: 'dono', rotulo: 'Responsável' },
                      { chave: 'tentativas', rotulo: 'Tentativas', numero: true },
                    ]}
                    linhas={pagina.itens.map((i) => ({
                      parado: idadeEmHorasPorExtenso(i.idade_em_horas),
                      falha: (
                        <>
                          {nomeDaRazao(i.razao)}
                          {!i.payload_disponivel && (
                            <span className="admin__aviso"> · sem conteúdo para reprocessar</span>
                          )}
                        </>
                      ),
                      origem: i.fonte,
                      dono: i.dono,
                      tentativas: i.tentativas,
                    }))}
                    chaveDe={(_, indice) => pagina.itens[indice]!.id}
                  />
                </div>

                {/*
                Linha de leitura, e não de navegação: `ListaLinha` faz da linha
                um botão, e daqui não se abre nada — reprocessar e resolver são
                da Cliqfy. Um botão que não leva a lugar nenhum é pior no dedo
                que no mouse, porque o alvo grande promete que leva.
              */}
                <ul className="admin__lista admin__so-cel">
                  {pagina.itens.map((i) => (
                    <li key={i.id} className="admin__lista-item">
                      <span className="admin__lista-titulo">{nomeDaRazao(i.razao)}</span>
                      <span className="admin__lista-quando">
                        {idadeEmHorasPorExtenso(i.idade_em_horas)}
                      </span>
                      <span className="admin__lista-meta">
                        <span>
                          {i.fonte} · {i.dono}
                        </span>
                        <span>
                          {i.tentativas} tentativa{i.tentativas === 1 ? '' : 's'}
                        </span>
                        {!i.payload_disponivel && (
                          <span className="admin__aviso">sem conteúdo para reprocessar</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {pagina.truncado && (
              <p className="admin__nota admin__nota--info">
                A lista encheu. Há mais itens além destes — filtre para chegar ao que procura.
              </p>
            )}

            <p className="admin__nota">
              Reprocessar e dar por resolvido são feitos pela Cliqfy, com registro de quem pediu e
              por quê. Os controles entram nesta tela junto com o step-up.
            </p>
          </>
        )}
      </Estado>
    </section>
  );
}

const RAZOES = [
  { chave: 'validacao', nome: 'Dado fora do formato' },
  { chave: 'permissao', nome: 'Sem permissão na engine' },
  { chave: 'schema_incompativel', nome: 'Formato mudou na origem' },
  { chave: 'regra_negocio', nome: 'Recusado por regra' },
  { chave: 'limite_retry', nome: 'Tentou até desistir' },
  { chave: 'engine_indisponivel', nome: 'Engine fora do ar' },
  { chave: 'desconhecido', nome: 'Falha não classificada' },
] as const;
/** Vocabulário do produto, não do sistema. `schema_incompativel` não é frase. */
function nomeDaRazao(chave: string): string {
  return RAZOES.find((r) => r.chave === chave)?.nome ?? chave;
}

/**
 * Horas viram a mesma linguagem do resto do produto.
 *
 * `idadeEmPalavras` recebe um instante, e o que a D10 devolve é uma duração —
 * converter aqui evita um segundo vocabulário de tempo na mesma tela.
 */
function idadeEmHorasPorExtenso(horas: number): string {
  return idadeEmPalavras(new Date(Date.now() - horas * 3_600_000).toISOString()) ?? 'agora';
}

// ── Peças pequenas ─────────────────────────────────────────────────────────
